import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';
const DEMO_NOTES_KEY = 'sfp.demoNotes.v1';

interface NotesDrawerProps {
  onClose: () => void;
}

/**
 * Per-user scratchpad — stored at `userNotes/{uid}`.
 * Autosaves on blur and every 4s of idle.
 */
export function NotesDrawer({ onClose }: NotesDrawerProps) {
  const { user } = useAuth();
  const [body, setBody] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    if (DEMO_MODE) {
      setBody(localStorage.getItem(DEMO_NOTES_KEY) ?? '');
      setLoaded(true);
      return;
    }
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'userNotes', user.uid));
        if (snap.exists()) setBody((snap.data() as any).body ?? '');
      } catch {
        /* ignore */
      } finally {
        setLoaded(true);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!loaded) return;
    setStatus('idle');
    const t = setTimeout(async () => {
      setStatus('saving');
      try {
        if (DEMO_MODE) {
          localStorage.setItem(DEMO_NOTES_KEY, body);
        } else if (user) {
          await setDoc(doc(db, 'userNotes', user.uid), { body, updatedAt: serverTimestamp() });
        }
        setStatus('saved');
      } catch {
        setStatus('idle');
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [body, loaded, user]);

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-[400px] flex-col border-l border-stone-200 bg-white shadow-2xl">
      <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-stone-800">Notes</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">
            {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : 'Idle'}
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded bg-stone-900 px-3 py-1 text-xs text-white">
          Close
        </button>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Personal scratchpad. Only you see this."
        className="flex-1 resize-none bg-stone-50 p-4 text-sm leading-relaxed text-stone-800 focus:outline-none"
      />
    </div>
  );
}
